/**
 * BatchReportTab — INPUT / OUTPUT grid áp full skill `/table-style`.
 * (Spec §3 — `Excel_Production_Report_Web_UI_Conversion_Spec.md`.)
 *
 * Mỗi section (INPUT / OUTPUT):
 *   - FilterBar global Search (quét toàn bộ TABLE_COLUMNS).
 *   - Per-column ▽ filter (text/checkboxes/date/number auto-detect).
 *   - CSS classes `app-dataframe` (compact) + `app-table-header-cell` +
 *     `app-table-cell-text` + `app-sticky-col` cho visual (skill /table-style).
 *   - Freeze CHỈ cột StackID (FROZEN_KEYS) — skill §7.6 (1 cột định danh).
 *   - Total row aggregation (SUM cho quantity, weighted-avg theo `mt` cho quality).
 *
 * Aggregation: SUM cho quantity/deducted/std qty, weighted-avg theo `mt`
 * cho quality + valuation. Cột `advanced` ẩn mặc định (toggle ngoài).
 */
import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useMemo, useState, type CSSProperties } from 'react'

import {
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  type ColumnFilterAnchorPosition,
} from '@/components/dataList'
import { useGetBatchReportInputQuery, useGetBatchReportOutputQuery } from '@/store/api/factoryBomApi'
import {
  INPUT_COLUMNS,
  OUTPUT_COLUMNS,
  type BatchCol,
  type BatchRow,
} from '../batchReportColumns'
import {
  formatAccessDate,
  fmtCurrency,
  fmtInt,
  fmtPercent,
  fmtPercentScaled,
  fmtPlain,
} from './formatters'
import { BlockTitle, ExLabel, ExVal } from './reportUi'
import { COLORS, neutralChipSx } from './styleTokens'

interface Props {
  showAdvanced: boolean
  factoryCode?: string
  /** PO đang chọn — INPUT load từ view v_batchreport_issue theo PO. */
  poNo?: string
  /** Header (Excel row1-3): Input Value = LIFFE + G2; Source (Access để trống). */
  inputValue?: number | null
  source?: string | null
}

/* ---------- formatters → string for cell ---------------------------------- */

function formatCell(value: unknown, kind: BatchCol['kind'], decimals?: number): string {
  if (value == null || value === '') return ''
  switch (kind) {
    case 'date': return formatAccessDate(value)
    case 'int': return fmtInt(value)
    case 'number': return fmtPlain(value, decimals ?? 2)
    case 'percent': return fmtPercent(value, decimals ?? 2)
    case 'percentScaled': return fmtPercentScaled(value, decimals ?? 2)
    case 'currency': return fmtCurrency(value)
    default: return String(value)
  }
}

function isNumericKind(kind: BatchCol['kind']): boolean {
  return kind === 'int' || kind === 'number' || kind === 'percent' || kind === 'percentScaled' || kind === 'currency'
}

/* ---------- aggregation -------------------------------------------------- */

function toNum(v: unknown): number {
  if (v == null || v === '') return 0
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function aggregateTotals(rows: BatchRow[], columns: BatchCol[]): BatchRow {
  const total: BatchRow = {}
  const mtTotal = rows.reduce((acc, r) => acc + toNum(r.mt), 0)
  for (const col of columns) {
    if (col.totalAgg === 'sum') {
      const sum = rows.reduce((acc, r) => acc + toNum(r[col.key]), 0)
      total[col.key] = sum
    } else if (col.totalAgg === 'wavg' && mtTotal > 0) {
      const wsum = rows.reduce((acc, r) => acc + toNum(r[col.key]) * toNum(r.mt), 0)
      total[col.key] = wsum / mtTotal
    } else {
      total[col.key] = ''
    }
  }
  total._isTotal = true
  return total
}

/* ---------- freeze (skill /table-style §7.6) ----------------------------
 * Chỉ freeze 1 cột định danh = StackID. Body/footer dùng class `app-sticky-col`
 * (CSS chung handle sticky + bg match row state); header dùng inline style
 * (sticky + zIndex thắng stacking). KHÔNG freeze nhiều cột. */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['stackId'])
/** Header freeze gold gradient (khớp `.app-table-header-cell`) — opaque chống
 *  bleed-through khi scroll ngang. */
const HEADER_FREEZE_BG = 'linear-gradient(180deg, #fff9f4 0%, #f4df9c 100%)'
/** Cột %Value + Value$ tô nền nhạt (tone teal TLC) để nổi khỏi khối quality. */
const VALUE_COL_KEYS: ReadonlySet<string> = new Set(['pctValue', 'value'])
const VALUE_COL_BG = '#d8ece9'

/* ---------- auto-fit column width --------------------------------------- *
 * Đo bề rộng text thực (canvas) để width mỗi cột vừa khít max(label, data,
 * total) — StackID không còn dư, cột Số co sát digit. Header cột Số dùng font
 * nhỏ hơn nên đo bằng font tương ứng. */
let _measureCtx: CanvasRenderingContext2D | null = null
function measureTextPx(text: string, font: string): number {
  if (typeof document === 'undefined') return text.length * 7
  if (!_measureCtx) _measureCtx = document.createElement('canvas').getContext('2d')
  if (!_measureCtx) return text.length * 7
  _measureCtx.font = font
  return _measureCtx.measureText(text).width
}
// Header cột Số (numeric) thu nhỏ để đỡ tốn không gian.
const NUMERIC_HEADER_FONT_REM = '0.66rem'

/* ---------- inferred filter kind cho mỗi cột ---------------------------- */

/** Bản đồ BatchCol → ColumnLike cho skill inferColumnFilterKind. */
function asFilterColumn(col: BatchCol) {
  // Skill chỉ nhận 'date'/'datetime'/'number'/'percent'. Currency/int → number;
  // text giữ nguyên cho skill auto-detect text vs checkboxes theo distinct.
  const skillKind =
    col.kind === 'date' ? 'date'
      : col.kind === 'percent' ? 'percent'
        : col.kind === 'currency' || col.kind === 'int' || col.kind === 'number' || col.kind === 'percentScaled' ? 'number'
          : 'text'
  return { key: col.key, label: col.label, kind: skillKind, filter: col.filter }
}

/* ---------- section component -------------------------------------------- */

interface SectionProps {
  title: 'INPUT' | 'OUTPUT'
  columns: BatchCol[]
  rows: BatchRow[]
  showAdvanced: boolean
  storageKeyPrefix: string
}

function BatchSection({ title, columns, rows: baseRows, showAdvanced }: SectionProps) {
  // Advanced OFF → hiện tới hết cột "Deducted", rồi thêm đúng 2 cột %Value +
  // Value$ (giữ nguyên thứ tự Excel). Advanced ON → hiện tất cả.
  const visibleColumns = useMemo(() => {
    if (showAdvanced) return columns
    const lastBasic = columns.findIndex((c) => c.key === 'deducted')
    const basic = lastBasic >= 0 ? columns.slice(0, lastBasic + 1) : columns.filter((c) => !c.advanced)
    const extras = columns.filter((c) => c.key === 'pctValue' || c.key === 'value')
    return [...basic, ...extras]
  }, [columns, showAdvanced])

  // Vị trí cột "Deducted" trong danh sách đang hiện → tô màu header cho mọi cột phía sau.
  const deductedIdx = useMemo(() => visibleColumns.findIndex((c) => c.key === 'deducted'), [visibleColumns])

  // --- Filter state (mỗi section có state riêng) -----------------------------
  const [searchTerm, setSearchTerm] = useState('')
  const searchPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length

  const clearAll = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    searchPopover.close()
  }

  // Search applies tất cả cột (kể cả advanced ẩn UI) — đảm bảo user gõ "BL-"
  // vẫn match StackID dù cột helper-sub đang ẩn.
  const searchableCols = columns
  const filteredRows = useMemo(
    () => baseRows.filter((r) =>
      rowMatchesSearch(r, searchTerm, searchableCols)
      && rowMatchesAllColumnFilters(r, columnFilters.filters),
    ),
    [baseRows, searchTerm, columnFilters.filters, searchableCols],
  )

  const total = useMemo(() => aggregateTotals(filteredRows, visibleColumns), [filteredRows, visibleColumns])

  // Auto width: vừa khít max(label, dữ liệu, total). Tính lại mỗi lần data /
  // cột đổi (kể cả lúc mở tab) → bám max text hiện tại.
  const colWidths = useMemo(() => {
    const fam =
      typeof document !== 'undefined'
        ? getComputedStyle(document.body).fontFamily || 'sans-serif'
        : 'sans-serif'
    // Đo hơi dư font (12.5px) + padding rộng để KHÔNG cắt số (ellipsis che digit).
    const bodyFont = `400 12.5px ${fam}`
    const headerFont = `700 12.5px ${fam}`
    const headerFontNum = `700 11px ${fam}` // ~0.66rem cho cột Số
    const ICON = 24 // filter ▽ + gap trong header
    const PADX = 28 // padding 2 bên cell + chút thở (tránh che số)
    const map = new Map<string, number>()
    for (const col of visibleColumns) {
      const numeric = isNumericKind(col.kind)
      let w = measureTextPx(col.label, numeric ? headerFontNum : headerFont) + ICON + PADX
      for (const r of filteredRows) {
        const t = formatCell(r[col.key], col.kind, col.decimals)
        if (t) w = Math.max(w, measureTextPx(t, bodyFont) + PADX)
      }
      if (col.totalAgg && col.totalAgg !== 'none') {
        const tt = formatCell(total[col.key], col.kind, col.decimals)
        if (tt) w = Math.max(w, measureTextPx(tt, bodyFont) + PADX)
      }
      const minW = col.minWidth ?? (numeric ? 54 : 64)
      map.set(col.key, Math.min(280, Math.max(minW, Math.ceil(w))))
    }
    return map
  }, [visibleColumns, filteredRows, total])
  const widthOf = (col: BatchCol) => colWidths.get(col.key) ?? col.width

  // Popover meta (computed khi mở col popover)
  const popoverColumn = useMemo(
    () => (colPopover ? columns.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover, columns],
  )
  const popoverKind = useMemo(
    () => (popoverColumn ? inferColumnFilterKind(asFilterColumn(popoverColumn), baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows, colPopover.key) : undefined),
    [popoverKind, colPopover, baseRows],
  )

  return (
    <Stack spacing={0.75}>
      {/* Section heading — BlockTitle style chốt ở BOM tab (thanh accent +
          uppercase primary), kèm chip số dòng + số cột. */}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography
          component="div"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            pl: 0.25,
            fontSize: '0.7rem',
            fontWeight: 800,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
            color: COLORS.primary,
          }}
        >
          <Box component="span" sx={{ width: 3, height: 13, borderRadius: 1, bgcolor: COLORS.primary }} />
          {title}
        </Typography>
        <Chip
          size="small"
          label={`${filteredRows.length.toLocaleString('en-US')}${filteredRows.length !== baseRows.length ? ` / ${baseRows.length}` : ''} rows`}
          sx={{ ...neutralChipSx, height: 20, fontSize: '0.7rem', fontWeight: 600 }}
        />
        <Typography sx={{ fontSize: '0.72rem', color: COLORS.textMuted }}>
          {visibleColumns.length} of {columns.length} columns
        </Typography>
      </Stack>

      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAll}
        activeFilterCount={activeFilterCount}
      >
        <FilterButton
          label="Search"
          active={!!searchTerm.trim()}
          onClick={(e) => searchPopover.open('search', e)}
        />
      </FilterBar>

      {/* Block theo chuẩn /table-style: card `app-dataframe` (viền + bo góc +
          gradient header) + rows xám MUI-default + freeze StackID qua
          `app-sticky-col`. KHÔNG dùng `app-table-wrap` legacy (gold stripe sẽ
          lệch với ô freeze — skill §7.6/§7.7). */}
      <Box className="app-dataframe app-dataframe--compact">
        <Box sx={{ flex: '1 1 auto', minHeight: 0, height: '100%', overflow: 'auto', bgcolor: '#fff' }}>
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0 }}>
            <TableHead>
              <TableRow>
                {visibleColumns.map((col, colIdx) => {
                  const numeric = isNumericKind(col.kind)
                  const isFrozen = FROZEN_KEYS.has(col.key)
                  // Cột nằm SAU "Deducted" → header tô màu khác để phân biệt khối
                  // valuation / deduction-factor / std với khối quality cơ bản.
                  const isPostDeducted = deductedIdx >= 0 && colIdx > deductedIdx
                  // Header freeze: inline style (sticky left + top + zIndex thắng
                  // các header cell khác z5) + opaque gradient chống bleed-through.
                  const headerStyle: CSSProperties = { width: widthOf(col), minWidth: widthOf(col), maxWidth: widthOf(col) }
                  if (isFrozen) {
                    headerStyle.position = 'sticky'
                    headerStyle.top = 0
                    headerStyle.left = 0
                    headerStyle.zIndex = 6
                    headerStyle.backgroundColor = '#fff9f4'
                    headerStyle.backgroundImage = HEADER_FREEZE_BG
                  }
                  // Màu nền header: %Value/Value$ teal, cột sau Deducted copper.
                  // CSS `.app-th-value`/`.app-th-postded` (specificity 4 class) thắng
                  // rule gradient !important của `.app-table-header-cell`.
                  const headerColorClass = isFrozen
                    ? ''
                    : VALUE_COL_KEYS.has(col.key)
                      ? ' app-th-value'
                      : isPostDeducted
                        ? ' app-th-postded'
                        : ''
                  return (
                    <TableCell
                      key={col.key}
                      align={numeric ? 'right' : 'left'}
                      className={`app-table-header-cell${headerColorClass}`}
                      style={headerStyle}
                      sx={{ whiteSpace: 'nowrap', ...(numeric ? { fontSize: NUMERIC_HEADER_FONT_REM } : {}) }}
                      title={col.label}
                    >
                      {col.label}
                      <ColumnFilterIcon
                        active={columnFilters.isActive(col.key)}
                        onClick={(position) => setColPopover({ key: col.key, position })}
                        ariaLabel={`Filter ${col.label}`}
                      />
                    </TableCell>
                  )
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography sx={{ fontSize: '0.8125rem', color: COLORS.textSubtle }}>
                      No rows match current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map((row, idx) => (
                  <TableRow key={idx} hover>
                    {visibleColumns.map((col) => {
                      const text = formatCell(row[col.key], col.kind, col.decimals)
                      const numeric = isNumericKind(col.kind)
                      const isFrozen = FROZEN_KEYS.has(col.key)
                      return (
                        <TableCell
                          key={col.key}
                          align={numeric ? 'right' : 'left'}
                          className={isFrozen ? 'app-sticky-col' : undefined}
                          style={{ width: widthOf(col), minWidth: widthOf(col), maxWidth: widthOf(col) }}
                          sx={{
                            whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis',
                            ...(VALUE_COL_KEYS.has(col.key) ? { bgcolor: VALUE_COL_BG } : {}),
                          }}
                          title={text}
                        >
                          <Box component="span" className="app-table-cell-text" sx={{ maxWidth: 'none' }}>
                            {text}
                          </Box>
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))
              )}

              {filteredRows.length > 0 ? (
                <TableRow>
                  {visibleColumns.map((col, idx) => {
                    const numeric = isNumericKind(col.kind)
                    const isFrozen = FROZEN_KEYS.has(col.key)
                    const showLabel = idx === 0
                    const text = col.totalAgg && col.totalAgg !== 'none'
                      ? formatCell(total[col.key], col.kind, col.decimals)
                      : showLabel ? 'Total' : ''
                    // Total row: sticky bottom; frozen cell thêm sticky left +
                    // bg subtle opaque (inline thắng app-sticky-col white).
                    const totalStyle: CSSProperties = { width: widthOf(col), minWidth: widthOf(col), maxWidth: widthOf(col) }
                    totalStyle.position = 'sticky'
                    totalStyle.bottom = 0
                    totalStyle.zIndex = isFrozen ? 4 : 3
                    if (isFrozen) {
                      totalStyle.left = 0
                      totalStyle.backgroundColor = COLORS.bgSubtle
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={numeric ? 'right' : 'left'}
                        style={totalStyle}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          bgcolor: VALUE_COL_KEYS.has(col.key) ? VALUE_COL_BG : COLORS.bgSubtle,
                          borderTop: `1px solid ${COLORS.borderStrong}`,
                          borderBottom: 'none',
                        }}
                      >
                        {text}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </Box>
      </Box>

      {/* Global Search popover */}
      <FilterPopover
        open={searchPopover.state !== null}
        anchorEl={searchPopover.state?.anchor ?? null}
        onClose={() => searchPopover.close()}
      >
        {searchPopover.isOpen('search') && (
          <SearchFilterContent
            value={searchTerm}
            onChange={(v) => setSearchTerm(v)}
            placeholder={`Search ${title.toLowerCase()} rows…`}
            onClear={() => searchPopover.close()}
          />
        )}
      </FilterPopover>

      {/* Per-column filter popover — duy nhất 1 instance per section. */}
      <ColumnFilterPopover
        open={colPopover !== null}
        anchorPosition={colPopover?.position ?? null}
        onClose={() => setColPopover(null)}
        label={popoverColumn?.label ?? ''}
        kind={popoverKind === 'none' ? 'text' : popoverKind}
        value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
        onChange={(v) => {
          if (!colPopover) return
          columnFilters.setFilter(colPopover.key, v)
        }}
        options={popoverOptions}
      />
    </Stack>
  )
}

/* ---------- tab entry ----------------------------------------------------- */

export default function BatchReportTab({ showAdvanced, factoryCode, poNo, inputValue, source }: Props) {
  const skip = !factoryCode || !poNo?.trim()
  // INPUT/OUTPUT load từ view (identity + qty theo PO; quality để trống):
  //   INPUT  ← v_batchreport_issue · OUTPUT ← v_batchreport_receive.
  const { data: inputData } = useGetBatchReportInputQuery(
    { factoryCode: factoryCode ?? '', poNo: poNo?.trim() ?? '' }, { skip },
  )
  const { data: outputData } = useGetBatchReportOutputQuery(
    { factoryCode: factoryCode ?? '', poNo: poNo?.trim() ?? '' }, { skip },
  )
  const inputApiRows = (inputData?.items ?? []) as BatchRow[]
  const outputApiRows = (outputData?.items ?? []) as BatchRow[]
  // Source = distinct origin_source ở INPUT (góp ý chị Đẹp) — ưu tiên prop, fallback tính tại chỗ.
  const srcFromInput = Array.from(
    new Set(inputApiRows.map((r) => String(r['originSource'] ?? '').trim()).filter(Boolean)),
  ).sort().join(' | ')
  const sourceText = source?.trim() || srcFromInput
  return (
    <Stack spacing={2}>
      {/* Overview (Excel "Batch Report" rows 1-3) — style copy từ BOM tab:
          bordered box + grid `label | value` + ExLabel/ExVal. */}
      <Box>
        <BlockTitle>Overview</BlockTitle>
        <Stack
          direction="row"
          spacing={4}
          useFlexGap
          flexWrap="wrap"
          alignItems="baseline"
          sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 1.5, bgcolor: COLORS.bgPage, px: 1.5, py: 1 }}
        >
          <Stack direction="row" spacing={1} alignItems="baseline">
            <ExLabel>PO No.</ExLabel>
            <ExVal>{poNo?.trim() || '—'}</ExVal>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="baseline">
            <ExLabel>Input Value</ExLabel>
            <ExVal>{inputValue == null ? '—' : (inputValue < 0
              ? `(${Math.abs(inputValue).toLocaleString('en-US', { maximumFractionDigits: 2 })})`
              : inputValue.toLocaleString('en-US', { maximumFractionDigits: 2 }))}</ExVal>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="baseline">
            <ExLabel>Source</ExLabel>
            <ExVal>{sourceText || '—'}</ExVal>
          </Stack>
        </Stack>
      </Box>

      <BatchSection
        title="INPUT"
        columns={INPUT_COLUMNS}
        rows={inputApiRows}
        showAdvanced={showAdvanced}
        storageKeyPrefix="batch-input"
      />
      <BatchSection
        title="OUTPUT"
        columns={OUTPUT_COLUMNS}
        rows={outputApiRows}
        showAdvanced={showAdvanced}
        storageKeyPrefix="batch-output"
      />
    </Stack>
  )
}
